import { Component, OnInit, OnDestroy } from '@angular/core';
import { RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { AiChatbot } from './shared/components/ai-chatbot/ai-chatbot';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, AiChatbot],
  templateUrl: './app.html',
  styleUrl: './app.scss'
})
export class App implements OnInit, OnDestroy {
  protected title = 'workluge_frontend_v2';
  private routerSubscription!: Subscription;

  constructor(private router: Router) { }

  ngOnInit() {
    // Monitor navigation to ensure proper theme handling
    this.routerSubscription = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.handleRouteChange(event.urlAfterRedirects);
    });
  }

  ngOnDestroy() {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  private handleRouteChange(url: string) {
    // Check if we're on a page that should NOT have themes applied
    const isPublicPage = this.isPublicPage(url);

    if (isPublicPage) {
      // Clear theme-background-active class for public pages (login, forgot password)
      this.clearThemeForPublicPages();
    } else {
      // For protected pages, ensure theme is properly restored if it should be active
      this.restoreThemeForProtectedPages();
    }

    // Handle sidebar visibility based on route data
    this.handleSidebarVisibility();
  }

  private isPublicPage(url: string): boolean {
    const publicRoutes = ['/login', '/forgotpassword', '/error-screen'];
    return publicRoutes.some(route => url.includes(route));
  }

  private clearThemeForPublicPages() {
    // Remove theme class and background from body for public pages
    const body = document.body;
    if (body.classList.contains('theme-background-active')) {
      body.classList.remove('theme-background-active');
      body.style.backgroundImage = '';
      console.log('Theme cleared for public page');
    }
  }

  private restoreThemeForProtectedPages() {
    // Check if theme should be active and restore it for protected pages
    const savedTheme = localStorage.getItem('selectedBackgroundTheme');
    const isThemeActive = localStorage.getItem('theme-background-active') === 'true';

    if (savedTheme && isThemeActive) {
      const body = document.body;
      if (!body.classList.contains('theme-background-active')) {
        // Restore theme for protected pages
        body.style.backgroundImage = `url(${savedTheme})`;
        body.style.backgroundSize = 'cover';
        body.style.backgroundPosition = 'center';
        body.style.backgroundRepeat = 'no-repeat';
        body.style.backgroundAttachment = 'fixed';
        body.classList.add('theme-background-active');
        console.log('Theme restored for protected page:', savedTheme);
      }
    }
  }

  handleChatbotAction(event: { action: string, data?: any }) {
    console.log('Chatbot action requested:', event);

    switch (event.action) {
      case 'open_task_form':
        // Emit global event to open task form on the current page
        this.emitGlobalEvent('openAddTaskForm', event.data);
        break;

      case 'open_project_form':
        // Emit global event to open project form on the current page
        this.emitGlobalEvent('openAddProjectForm', event.data);
        break;

      case 'open_add_project_form':
        // Emit global event to open add project form
        this.emitGlobalEvent('openAddProjectForm', event.data);
        break;

      case 'open_add_task_form':
        // Emit global event to open add task form
        this.emitGlobalEvent('openAddTaskForm', event.data);
        break;

      case 'search_and_open_project':
        // Emit global event to search and open project
        this.emitGlobalEvent('searchAndOpenProject', event.data);
        break;

      case 'search_and_open_project_by_name':
        // Emit global event to search and open project by name
        this.emitGlobalEvent('searchAndOpenProject', event.data.projectName);
        break;

      case 'navigate_and_search_project':
        // This will be handled by the navigate_and_search_project action in chatbot
        // The navigation happens first, then search_and_open_project is triggered
        break;

      default:
        console.log('Unknown chatbot action:', event.action);
    }
  }

  private emitGlobalEvent(eventName: string, data?: any) {
    // Create and dispatch a custom event that can be listened to by other components
    const customEvent = new CustomEvent(eventName, {
      detail: data,
      bubbles: true
    });
    document.dispatchEvent(customEvent);
  }

  private handleSidebarVisibility() {
    // Get current route data to check for hideSidebar property
    const currentRoute = this.router.routerState.snapshot.root;
    const routeData = this.getRouteData(currentRoute);
    
    const body = document.body;
    
    if (routeData && routeData['hideSidebar']) {
      // Hide sidebar for this route
      body.classList.add('route-hide-sidebar');
      console.log('Sidebar hidden for route with data:', routeData);
      console.log('Body classes after hiding sidebar:', body.classList.toString());
    } else {
      // Show sidebar for this route
      body.classList.remove('route-hide-sidebar');
      console.log('Sidebar shown for route');
      console.log('Body classes after showing sidebar:', body.classList.toString());
    }
  }

  private getRouteData(route: any): any {
    if (route.data) {
      return route.data;
    }
    
    if (route.children) {
      for (const child of route.children) {
        const data = this.getRouteData(child);
        if (data) {
          return data;
        }
      }
    }
    
    return null;
  }
}
